import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rows", "template"]
  static values = {
    addLabel: String,
    removeLabel: String
  }

  connect() {
    this.updateRowActions()
    this.syncLookupOptions()
  }

  addRow(event) {
    event.preventDefault()

    const fragment = this.templateTarget.content.cloneNode(true)
    this.rowsTarget.appendChild(fragment)

    this.updateRowActions()
    this.syncLookupOptions()

    const lastRow = this.rowsTarget.querySelector("[data-roster-participants-row]:last-child")
    this.field(lastRow, "first_name")?.focus()
  }

  removeRow(event) {
    event.preventDefault()

    const row = event.target.closest("[data-roster-participants-row]")
    if (!row) return

    const rows = this.rowsTarget.querySelectorAll("[data-roster-participants-row]")
    if (rows.length == 1) {
      this.clearRow(row)
      this.syncLookupOptions()
      return
    }

    row.remove()
    this.updateRowActions()
    this.syncLookupOptions()
  }

  firstNameChanged(event) {
    const row = event.target.closest("[data-roster-participants-row]")
    if (!row) return

    const participantIdField = this.field(row, "participant_id")
    const currentParticipantId = participantIdField.value
    const option = this.lookupOption(event.target, event.target.value)

    if (!option || this.optionTakenByOtherRow(option, currentParticipantId)) {
      participantIdField.value = ""
      this.syncLookupOptions()
      return
    }

    participantIdField.value = option.dataset.participantId || ""
    this.field(row, "first_name").value = option.dataset.firstName || ""
    this.field(row, "last_name").value = option.dataset.lastName || ""
    this.field(row, "email").value = option.dataset.email || ""
    this.syncLookupOptions()
  }

  manualInput(event) {
    const row = event.target.closest("[data-roster-participants-row]")
    if (!row) return

    this.field(row, "participant_id").value = ""
    this.syncLookupOptions()
  }

  field(row, name) {
    return row.querySelector(`[data-participant-field='${name}']`)
  }

  lookupOption(lookupInput, value) {
    const datalistId = lookupInput.getAttribute("list")
    if (!datalistId) return null

    const datalist = document.getElementById(datalistId)
    if (!datalist) return null

    return Array.from(datalist.options).find((option) => option.value === value)
  }

  clearRow(row) {
    this.field(row, "participant_id").value = ""
    this.field(row, "first_name").value = ""
    this.field(row, "last_name").value = ""
    this.field(row, "email").value = ""
  }

  syncLookupOptions() {
    this.clearDuplicateSelections()

    const datalist = this.lookupDatalist()
    if (!datalist) return

    const selectedIds = this.selectedParticipantIds()

    Array.from(datalist.options).forEach((option) => {
      const participantId = option.dataset.participantId
      const selected = participantId ? selectedIds.has(participantId) : false
      option.disabled = selected
      option.hidden = selected
    })
  }

  optionTakenByOtherRow(option, currentParticipantId) {
    const participantId = option.dataset.participantId
    if (!participantId) return false
    if (participantId == currentParticipantId) return false

    return this.selectedParticipantIds().has(participantId)
  }

  clearDuplicateSelections() {
    const seen = new Set()

    this.rowsTarget.querySelectorAll("[data-roster-participants-row]").forEach((row) => {
      const participantIdField = this.field(row, "participant_id")
      if (!participantIdField || participantIdField.value == "") return
      if (!seen.has(participantIdField.value)) {
        seen.add(participantIdField.value)
        return
      }

      this.clearRow(row)
    })
  }

  selectedParticipantIds() {
    const ids = Array.from(this.rowsTarget.querySelectorAll("[data-participant-field='participant_id']"))
      .map((field) => field.value)
      .filter((value) => value != "")

    return new Set(ids)
  }

  lookupDatalist() {
    const lookup = this.rowsTarget.querySelector("[data-participant-field='first_name']")
    const datalistId = lookup?.getAttribute("list")
    return datalistId ? document.getElementById(datalistId) : null
  }

  updateRowActions() {
    const rows = this.rowsTarget.querySelectorAll("[data-roster-participants-row]")

    rows.forEach((row, index) => {
      const button = row.querySelector("[data-roster-participants-row-action-button]")
      if (!button) return

      const isLastRow = index == rows.length - 1
      if (isLastRow) {
        button.textContent = this.addLabelValue
        button.classList.remove("text-error")
        button.setAttribute("data-action", "roster-participants#addRow")
      } else {
        button.textContent = this.removeLabelValue
        button.classList.add("text-error")
        button.setAttribute("data-action", "roster-participants#removeRow")
      }
    })
  }
}
